import { Controller } from "@hotwired/stimulus"

// CloudFront経由でevent/data/yohei.jsonをfetchし、EVENTセクション/eventページ双方の
// カードDOMを実行時に組み立てる。ホームでは最新3件（displayCountValue）、
// /eventページでは全件（displayCountValue省略）を表示する。
export default class extends Controller {
  static targets = ["list"]

  static values = {
    cloudfrontUrl: String, // data-event-cloudfront-url-value から注入される（末尾スラッシュなし）
    displayCount: { type: Number, default: 0 } // 0 = 全件表示
  }

  // Turbo遷移ではbodyが差し替わるだけでJSモジュールは再読み込みされないため、
  // ここに載せたキャッシュはページ間・再訪問間で保持される。
  // これが無いと遷移のたびにhidden→fetch待ち→表示の高さジャンプが発生し、
  // 連打するとフッターが跳ねて見える不具合になる。
  static cachedPosts = null

  connect() {
    this.abortController = new AbortController()

    if (this.constructor.cachedPosts) {
      this.applyPosts(this.constructor.cachedPosts)
      return
    }

    // 取得が終わるまでセクションは表示したまま（CSS側で高さを確保）にしておく。
    // ここでhiddenにすると取得完了までページ丈が縮み、フッターが
    // ヘッダー直後に来てから取得完了時に一気に下へ飛ぶ問題が起きる。
    this.load()
  }

  disconnect() {
    // 描画完了前にDOMから外れた場合に備えてfetchを中断する
    this.abortController.abort()
  }

  async load() {
    let data
    try {
      const response = await fetch(`${this.cloudfrontUrlValue}/event/data/yohei.json`, {
        signal: this.abortController.signal
      })
      if (!response.ok) {
        this.element.hidden = true
        return
      }

      data = await response.json()
    } catch {
      // fetch失敗・abort・JSON parse失敗は非表示にして終了させる
      this.element.hidden = true
      return
    }

    if (!Array.isArray(data?.posts)) {
      this.element.hidden = true
      return
    }

    this.constructor.cachedPosts = data.posts
    this.applyPosts(data.posts)
  }

  applyPosts(allPosts) {
    const posts = this.displayCountValue > 0 ? allPosts.slice(0, this.displayCountValue) : allPosts
    if (posts.length === 0) {
      this.element.hidden = true
      return
    }

    this.render(posts)
    this.element.hidden = false
  }

  render(posts) {
    this.listTarget.replaceChildren(...posts.map((post, index) => this.buildCard(post, index)))
  }

  buildCard(post, index) {
    const { date, title, content } = this.parseCaption(post.caption)

    const link = document.createElement("a")
    link.className = "event-card"
    link.href = post.permalink
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    link.dataset.analyticsInstagramContentId = post.id ?? ""
    link.dataset.analyticsContentTitle = title
    link.dataset.analyticsContentPosition = String(index + 1)

    const imageWrap = document.createElement("div")
    imageWrap.className = "event-card__image-wrap"

    const image = document.createElement("img")
    image.className = "event-card__image"
    image.src = post.image_url
    image.alt = ""
    image.loading = "lazy"
    imageWrap.appendChild(image)

    const body = document.createElement("div")
    body.className = "event-card__body"

    const dateEl = document.createElement("p")
    dateEl.className = "event-card__date"
    dateEl.textContent = date

    const titleEl = document.createElement("p")
    titleEl.className = "event-card__title"
    titleEl.textContent = title

    const contentEl = document.createElement("p")
    contentEl.className = "event-card__content"
    contentEl.textContent = content

    body.appendChild(dateEl)
    body.appendChild(titleEl)
    body.appendChild(contentEl)

    link.appendChild(imageWrap)
    link.appendChild(body)

    return link
  }

  // yoheiの投稿キャプションは1行目=日付、2行目=タイトル、空行を挟んで3行目以降=内容、
  // という書式で運用されているため、改行位置でそのまま分割する
  parseCaption(caption) {
    const lines = (caption ?? "").split("\n")
    const date = lines[0] ?? ""
    const title = lines[1] ?? ""
    const rest = lines.slice(2)
    const content = (rest[0] === "" ? rest.slice(1) : rest).join("\n").trim()

    return { date, title, content: this.truncate(content, 100) }
  }

  truncate(text, maxLength) {
    return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text
  }
}
